import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';

import { groupBy } from 'lodash'
import { Trash } from 'lucide-react';
import { ButtonIcon } from 'twyne-new.ui';

import Select from '@Components/formsy/Select';

import RuleGroupItemWrapper from '../pure/rules/RuleGroup/Item';

import Inputs from './Input';
import { getEqualityOptions } from './utils';

const propTypes = {
  data: PropTypes.object,
  options: PropTypes.object,
  onDeleteRow: PropTypes.func,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
}

export default function Item({ data, options, onDeleteRow, onChange, disabled }) {
  return <div className='flex items-center gap-4'>
    <Property data={data} options={options} onChange={onChange} disabled={disabled} />
    <Equality data={data} options={options} onChange={onChange} disabled={disabled} />
    <Value data={data} options={options} onChange={onChange} disabled={disabled} />
    <ButtonIcon content='Delete' onClick={onDeleteRow} disabled={disabled}>
      <Trash size={16} />
    </ButtonIcon>
  </div>
}

function Property({ data, options, onChange, disabled }) {
  const { key } = data

  const getLabelOption = useCallback(group => {
    return { value: 'group', label: `-- ${group} --`, group, disabled: true }
  }, [])

  const propOptions = useMemo(() => {
    const groups = groupBy(options, 'group')
    const grouped = []
    Object.entries(groups).forEach(([ group, options ]) => grouped.push(getLabelOption(group), ...options))
    return grouped
  }, [options])

  const handleChange = option => {
    const updated = Object.assign(data, { key: option.value, type: option.type })
    delete updated.value
    delete updated.equality
    if (option.type === 'select') {
      updated.options = option.options
    }
    onChange(updated)
  }

  return <div className='w-full'>
    <RuleGroupItemWrapper>
      <Select name={`${key}-option`} value={key} options={propOptions}
        onChange={handleChange} disabled={disabled} clearable={false} required />
    </RuleGroupItemWrapper>
  </div>
}

function Equality ({ data, options, onChange, disabled }) {
  const { key, type, equality } = data
  const { equalities } = options.find(option => option.value === key) || {}
  const propOptions = equalities ? equalities : useMemo(() => getEqualityOptions(type), [type])

  const handleChange = option => {
    if (option) {
      let newData = Object.assign(data, { equality: option.value })
      if (['missing', 'not-missing'].includes(option.value)) {
        newData = Object.assign(newData, { value: option })
      }
      onChange(newData)
    }
  }

  if (data.type === 'boolean' && !data.equality) {
    data.equality = "equal"
  }

  if (data.type === 'gender') {
    return null
  }

  return <div className='w-full'>
    <RuleGroupItemWrapper>
      <Select
        name='equality-key'
        value={equality}
        options={propOptions}
        clearable={false}
        disabled={key === 'pid' && disabled ? false : disabled}
        onChange={option => handleChange(option)}
        required
      />
    </RuleGroupItemWrapper>
  </div>
}

function Value({ data, options, onChange, disabled, props }) {
  const { key, equality } = data

  const handleChange = value => {
    const newData = Object.assign(data, { value })
    onChange(newData)
  }

  if (['missing', 'not-missing'].includes(equality)) {
    return null
  }

  return <div className='w-full'>
    <RuleGroupItemWrapper>
      <Inputs
        name={`${key}-key`}
        data={data}
        options={options}
        disabled={key === 'pid' && disabled ? false : disabled}
        onChange={handleChange}
        {...props}
      />
    </RuleGroupItemWrapper>
  </div>
}

Item.propTypes = propTypes
Item.defaultProps = {}
